/**
 * Data Reliability v2 — composite assembly.
 *
 * Takes a set of ComponentResult instances and a weight map, returns the
 * weighted-average score plus the components annotated with their
 * post-renormalization weights.
 *
 * Renormalization rule (DEV-846): inactive components do NOT contribute.
 * Their declared weight is redistributed across the remaining applicable
 * components proportionally. So if a composite has 4 components with equal
 * 25% weights and one is inactive, the remaining three each become 33%.
 *
 * Example:
 *   Outlook configured with 25/25/25/25 weights for
 *     [latency_trend, engagement, drift_risk, preflight_risk].
 *   PreFlightRisk is N/A because the project has no PreFlight items.
 *   Renormalized weights for the 3 remaining: 33.3% each.
 *   Composite score = (latency_trend × 0.333) + (engagement × 0.333)
 *                   + (drift_risk × 0.333).
 *
 * See: DEV-846 epic, DEV-847.
 */

import type {
  ComponentResult,
  CompositeResult,
  CompositeKey,
  ComponentKey,
} from './types';

/* ------------------------------------------------------------------------- */
/*  Color band                                                               */
/* ------------------------------------------------------------------------- */

/**
 * Standard 0-3 color band consistent with Mission and Summary.
 *
 * Centralizing rationale (DEV-814 / DEV-846): the original DataReliabilityTab
 * had its own 80/60 thresholds duplicated from the engine's KPI_DEFAULTS.
 * Drift opportunity. This is the single mapping for reliability composites.
 */
function colorFromScore(score: number | null): 0 | 1 | 2 | 3 {
  if (score === null || !Number.isFinite(score)) return 0;
  if (score >= 80) return 3;
  if (score >= 60) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Trend                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Trend direction from current vs previous score. Conservative epsilon
 * (3 points) to avoid trend-noise from rounding or single-period tiny
 * fluctuations — reliability moves slowly under steady operations, so
 * sub-3-point swings are usually noise rather than meaningful change.
 */
function trendFromDelta(
  current: number | null,
  previous: number | null,
  epsilon: number = 3,
): -1 | 0 | 1 {
  if (
    current === null ||
    previous === null ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous)
  ) {
    return 0;
  }
  const delta = current - previous;
  if (delta > epsilon) return 1;
  if (delta < -epsilon) return -1;
  return 0;
}

/* ------------------------------------------------------------------------- */
/*  Weighted average with renormalization                                    */
/* ------------------------------------------------------------------------- */

/**
 * Compute the weighted average across applicable components, redistributing
 * inactive-component weight proportionally.
 *
 * Returns the composite score (rounded 0-100, or null if NO components
 * are applicable) AND the components annotated with their post-
 * renormalization weight. Inactive components are passed through unchanged
 * so the UI can show them as N/A in drill-in tooltips.
 *
 * Defensive: if declared weights don't sum to 1.0, the renormalization
 * step still produces a valid weighted-average — it normalizes against
 * whatever sum is provided. Caller (DEV-849 config UI) should enforce
 * the sum-to-1 invariant; engine doesn't reject malformed weights.
 */
function composeWeightedAverage(
  components: ComponentResult[],
  declaredWeights: Partial<Record<ComponentKey, number>>,
): { score: number | null; annotated: ComponentResult[] } {
  const applicable = components.filter(
    (c) => c.applicable && c.score !== null && Number.isFinite(c.score),
  );

  if (applicable.length === 0) {
    // All components inactive — composite has no signal to compute.
    return { score: null, annotated: components };
  }

  const totalDeclaredWeight = applicable.reduce(
    (sum, c) => sum + Math.max(0, declaredWeights[c.key] ?? 0),
    0,
  );

  if (totalDeclaredWeight <= 0) {
    // All applicable components have zero (or negative) declared weight —
    // fall back to equal weighting so we still produce a meaningful number.
    const equalWeight = 1 / applicable.length;
    let score = 0;
    const annotated = components.map((c) => {
      if (c.applicable && c.score !== null) {
        score += c.score * equalWeight;
        return { ...c, weight_normalized: equalWeight };
      }
      return c;
    });
    return { score: Math.round(score), annotated };
  }

  // Normal path — renormalize declared weights against the applicable subset.
  let score = 0;
  const annotated = components.map((c) => {
    if (!c.applicable || c.score === null) return c;
    const declared = Math.max(0, declaredWeights[c.key] ?? 0);
    const wn = declared / totalDeclaredWeight;
    score += c.score * wn;
    return { ...c, weight_normalized: wn };
  });

  return { score: Math.round(Math.max(0, Math.min(100, score))), annotated };
}

/* ------------------------------------------------------------------------- */
/*  Public API                                                               */
/* ------------------------------------------------------------------------- */

/**
 * Build a CompositeResult from its components and applicability.
 *
 * If the composite-level applicability is false, returns a null-score
 * composite with the supplied reason — components are still returned
 * (caller may want to surface them in drill-in even when the composite
 * is N/A, e.g. to show what's blocking applicability).
 */
export function buildComposite(args: {
  key: CompositeKey;
  components: ComponentResult[];
  weights: Partial<Record<ComponentKey, number>>;
  applicable: boolean;
  reason?: string;
  previousScore?: number | null;
}): CompositeResult {
  const { key, components, weights, applicable, reason, previousScore } = args;

  if (!applicable) {
    return {
      key,
      score: null,
      color: 0,
      trend: 0,
      applicable: false,
      reason,
      components,
    };
  }

  const { score, annotated } = composeWeightedAverage(components, weights);

  return {
    key,
    score,
    color: colorFromScore(score),
    trend: trendFromDelta(score, previousScore ?? null),
    applicable: true,
    components: annotated,
  };
}

/* ------------------------------------------------------------------------- */
/*  Backward-compat composite                                                */
/* ------------------------------------------------------------------------- */

/**
 * Single-number score for legacy consumers that haven't migrated to display
 * Now and Outlook explicitly. Conservative — prefers the lagging score
 * when applicable, falls back to leading, then null.
 *
 * DEV-851 migrates each consumer; this exists only to prevent breakage
 * during the rollout.
 */
export function deriveLegacyComposite(
  now: CompositeResult,
  outlook: CompositeResult,
): { score: number | null; color: 0 | 1 | 2 | 3 } {
  if (now.applicable && now.score !== null) {
    return { score: now.score, color: now.color };
  }
  if (outlook.applicable && outlook.score !== null) {
    return { score: outlook.score, color: outlook.color };
  }
  return { score: null, color: 0 };
}
